/**
 * @flow
 */

import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { formatNumber } from 'libphonenumber-js';
import { pick, omit, findIndex, get } from 'lodash';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon } from 'semantic-ui-react';

import { Modal, Button } from '../../../../components';
import SupplierForm from '../form/SupplierForm';
import useUserLookup from '../../../../lib/useUserLookup';
import { GET_SUPPLIERS_QUERY, UPDATE_SUPPLIER_QUERY } from '../../containers/queries';

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.suppliers.updateSupplier.existingsError',
        defaultMessage: 'This supplier does not exists',
    },
    phoneExistingsError: {
        id: 'pages.suppliers.updateSupplier.phoneExistingsError',
        defaultMessage: 'Supplier with the same phone number already exists',
    },
    emailExistingsError: {
        id: 'pages.suppliers.updateSupplier.emailExistingsError',
        defaultMessage: 'Supplier with the same email already exists',
    },
    emailPhoneError: {
        id: 'pages.suppliers.updateSupplier.emailPhoneError',
        defaultMessage:
            'There are two different users with the provided phone and email',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const formatPhone = phone => (phone ? formatNumber(phone, 'International') : '+371');

type PersonalInformationProps = {
    supplier: Object,
    open: boolean,
    onClose: Function,
    onSupplierUpdate?: Function,
    intl: Object,
};

function PersonalInformation({ supplier, open, onClose, onSupplierUpdate, intl }: PersonalInformationProps) {
    const searchQuery = useSelector(({ suppliers }) => suppliers.get('searchQuery'));
    const { userLookupStatus, userLookupUser, onUserLookup, resetUserLookup } = useUserLookup();

    const buildInitialValues = () => ({
        firstName: supplier.firstName || '',
        lastName: supplier.lastName || '',
        email: supplier.email || '',
        phone: formatPhone(supplier.phone),
        password: '',
        repeatPassword: '',
    });

    const methods = useForm({ defaultValues: buildInitialValues() });
    const {
        handleSubmit,
        reset,
        setError,
        getValues,
        formState: { isSubmitting, isDirty },
    } = methods;

    // Reinitialize when supplier or open changes
    useEffect(() => {
        if (open) {
            reset(buildInitialValues());
        }
    }, [open, supplier.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // When userLookupUser is found, fill in their data
    useEffect(() => {
        if (open && userLookupUser) {
            const fields = ['email', 'firstName', 'lastName'];
            reset({
                ...getValues(),
                ...pick(userLookupUser, fields),
                phone: formatPhone(userLookupUser.phone),
            });
        }
    }, [userLookupUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const [updateSupplier] = useMutation(UPDATE_SUPPLIER_QUERY, {
        update: (store, response) => {
            try {
                const updatedSupplier = get(response, 'data.updateSupplier.supplier');
                if (updatedSupplier && updatedSupplier.id !== supplier.id) {
                    const variables = {
                        search: searchQuery || undefined,
                        first: 20,
                        resolveRole: 'SYSADMIN',
                    };
                    const data = store.readQuery({
                        query: GET_SUPPLIERS_QUERY,
                        variables,
                    });
                    const idx = findIndex(
                        data.viewer.suppliers.edges,
                        edge => edge.node.id === supplier.id,
                    );
                    if (idx !== -1) {
                        data.viewer.suppliers.edges[idx].node = updatedSupplier;
                    }
                    store.writeQuery({
                        query: GET_SUPPLIERS_QUERY,
                        variables,
                        data,
                    });
                }
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onSubmit = async formData => {
        const input = Object.assign(
            {
                supplierId: supplier.id,
                phone: (formData.phone || '').replace(/[^+\d]/g, ''),
            },
            pick(formData, ['firstName', 'lastName', 'email', 'password']),
        );

        try {
            const { data } = await updateSupplier({
                variables: { input },
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateSupplier: {
                        __typename: 'UpdateSupplierPayload',
                        supplier: {
                            __typename: 'Supplier',
                            ...input,
                        },
                    },
                },
            });

            if (onSupplierUpdate) {
                onSupplierUpdate(data.updateSupplier.supplier);
            }
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'NotExists') {
                    if (graphQLError.data?.error === 'ID_DOES_NOT_EXISTS') {
                        setError('root', { message: intl.formatMessage(intlMessages.existingsError) });
                        return;
                    }
                } else if (graphQLError.name === 'AlreadyExists') {
                    if (graphQLError.data?.error === 'PHONE_ALREADY_EXISTS') {
                        setError('phone', { message: intl.formatMessage(intlMessages.phoneExistingsError) });
                        return;
                    } else if (graphQLError.data?.error === 'EMAIL_ALREADY_EXISTS') {
                        setError('email', { message: intl.formatMessage(intlMessages.emailExistingsError) });
                        return;
                    }
                } else if (graphQLError.name === 'Forbidden') {
                    if (graphQLError.data?.error === 'THERE_ARE_TWO_DIFFERENT_USER_WITH_PROVIDED_PHONE_AND_EMAIL') {
                        setError('root', { message: intl.formatMessage(intlMessages.emailPhoneError) });
                        return;
                    }
                }
                setError('root', { message: graphQLError.message || intl.formatMessage(intlMessages.defaultErrorMessage) });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    const readOnly = userLookupStatus === 'EXISTS';

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    <SupplierForm
                        readOnly={readOnly}
                        mode="edit"
                        userLookupStatus={userLookupStatus}
                        userLookupUser={userLookupUser}
                        onUserLookup={onUserLookup}
                        resetUserLookup={resetUserLookup}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.suppliers.editSupplier.button.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.suppliers.editSupplier.button.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(PersonalInformation);
